import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { KlydoError } from './types';
import useHandleErrorMutation from './useHandleErrorMutation';

export type UseKlydoMutationOptions<
  TData = unknown,
  TVariables = void,
  TContext = unknown,
> = UseMutationOptions<TData, KlydoError, TVariables, TContext> & {
  suppressErrorAlert?: boolean;
};

export type UseKlydoMutationResult<
  TData = unknown,
  TError = KlydoError,
  TVariables = void,
  TContext = unknown,
> = UseMutationResult<TData, TError, TVariables, TContext>;

const useKlydoMutation = <
  TData = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: UseKlydoMutationOptions<TData, TVariables, TContext>,
): UseKlydoMutationResult<TData, KlydoError, TVariables, TContext> => {
  const handleError = useHandleErrorMutation();
  const queryClient = useQueryClient();

  const enhancedOptions = {
    ...options,
    onError: (
      error: KlydoError,
      variables: TVariables,
      context: TContext | undefined,
    ) => {
      // 409 means the server refused because THIS VIEW is stale — the resource
      // moved on while it was open (e.g. an admin approved the publication
      // request this screen still shows as pending). Whatever was on screen is
      // wrong, so resync every query rather than guess which caches are
      // affected; the api's conflict message still surfaces through the alert
      // below. Runs even with suppressErrorAlert — suppressing a toast must not
      // leave the user acting on stale state.
      if (error.response?.status === HttpStatusCode.Conflict) {
        queryClient.invalidateQueries();
      }
      if (!options.suppressErrorAlert) {
        handleError(error, 'An unxpected error occurred');
      }
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
  };

  return useMutation(enhancedOptions);
};

export default useKlydoMutation;
